import { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';
import type { AuditRecord } from '@peles/shared';
import { useEntityAudit } from '../../hooks/useAudit';
import { EntityModal } from './EntityModal';
import { AuditTrail } from './AuditTrail';

interface AuditInfoTooltipProps {
  entityType: string;
  entityId: string;
}

/**
 * Who made this record and who last touched it, with the full trail one click
 * further in.
 *
 * The summary answers the question that gets asked ninety per cent of the time
 * without spending a modal on it; the trail behind it is where the actual
 * field-by-field changes, the device and the location live.
 */
export function AuditInfoTooltip({ entityType, entityId }: AuditInfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const [showTrail, setShowTrail] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // The trail is fetched once the popover opens, and stays fetched for the
  // modal — same query key, so opening the full history costs nothing more.
  const { data: entries, isLoading } = useEntityAudit(entityType, entityId, open || showTrail);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Records arrive newest first, so the first create is the creation and the
  // first update is the most recent one. Actions are lowercase in the trail —
  // the backend normalises them on write so the filters have one bucket per
  // verb rather than three.
  const createEntry = entries?.find((e) => e.action === 'create' || e.action === 'bulk_create');
  const updateEntry = entries?.find(
    (e) => e.action === 'update' || e.action === 'status_change' || e.action === 'deactivate',
  );

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="text-gray-400 hover:text-primary-600 p-1"
        aria-label="Audit info"
        title="Audit info"
      >
        <Info size={16} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-72 text-sm">
          <h4 className="font-semibold text-gray-800 mb-3">Audit Info</h4>
          {isLoading ? (
            <p className="text-gray-500">Loading...</p>
          ) : entries && entries.length > 0 ? (
            <div className="space-y-2">
              <AuditSummaryRow label="Created" record={createEntry} />
              {updateEntry && (
                <div className="border-t border-gray-100 pt-2 mt-2">
                  <AuditSummaryRow label="Updated" record={updateEntry} />
                </div>
              )}
              <button
                type="button"
                className="mt-2 w-full text-left text-primary-600 hover:underline"
                onClick={() => {
                  setOpen(false);
                  setShowTrail(true);
                }}
              >
                {`View full history (${entries.length})`}
              </button>
            </div>
          ) : (
            <p className="text-gray-500">No audit records found</p>
          )}
        </div>
      )}

      <EntityModal open={showTrail} onClose={() => setShowTrail(false)} title="History" size="md">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : (
          <AuditTrail records={entries ?? []} />
        )}
      </EntityModal>
    </div>
  );
}

function AuditSummaryRow({ label, record }: { label: string; record?: AuditRecord }) {
  if (!record) return null;
  const who = record.actor?.name ?? record.actor?.email ?? 'System';

  return (
    <>
      <div className="flex justify-between">
        <span className="text-gray-500">{`${label} by`}</span>
        <span className="text-gray-800">{who}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-gray-500">{`${label} at`}</span>
        <span className="text-gray-800">{new Date(record.occurredAt).toLocaleString()}</span>
      </div>
    </>
  );
}
